import { AssetSubClassName } from '@api/api/entity-constants';
import { Entity, MappedEntity } from '@api/api/entity-schema';
import { PriceHoverData, TimeSeriesItem } from '@chart';
import type { PriceChangeParams } from '@helpers/utils/asset-price/asset-price';
import {
  EST_TIME_ZONE,
  getPriceChange,
} from '@helpers/utils/asset-price/asset-price';
import {
  getLivePriceDataKey,
  LivePriceData,
  LivePriceInfo,
  mapLivePriceData,
  PriceEntity,
} from '@terminal/hooks/use-live-price';

export type { PriceChangeParams };
export { EST_TIME_ZONE, getPriceChange };

export function getTickerForLivePrice(
  entity: Pick<Entity, 'subscribable_ticker'>
) {
  return entity.subscribable_ticker;
}

export const isPriceSnake = (entity: MappedEntity, snakeName = '') =>
  snakeName === entity.default_snake;

export const isFX = (subClass: string) =>
  subClass === AssetSubClassName.MajorCross ||
  subClass === AssetSubClassName.GenericCross;

export const getPriceData = ({
  hoverData,
  livePriceData,
  entity,
  ts,
  priceEntity,
}: {
  hoverData?: PriceHoverData;
  livePriceData: LivePriceData;
  entity: MappedEntity;
  ts: TimeSeriesItem[];
  priceEntity?: PriceEntity;
}): LivePriceInfo => {
  const entityWithCountry = {
    ...entity,
    trading_country: entity.trading_country,
  };
  const livePriceDataKey = getLivePriceDataKey(entity) ?? '';

  if (hoverData?.currentPrice && hoverData?.previousPrice) {
    return mapLivePriceData({
      entity: entityWithCountry,
      lastKnownPrice: hoverData.currentPrice.close,
      priceBefore:
        hoverData.previousPrice.close || hoverData.currentPrice.close,
      time: hoverData.currentPrice.time,
    });
  }

  if (!livePriceData[livePriceDataKey] && ts.length) {
    const currentPrice = ts[ts.length - 1];
    const previousPrice = ts[ts.length - 2] ?? currentPrice;
    return mapLivePriceData({
      entity: entityWithCountry,
      lastKnownPrice: currentPrice.close,
      priceBefore: previousPrice.close || currentPrice.close,
      time: currentPrice.time,
    });
  }

  if (!livePriceData[livePriceDataKey] && priceEntity) {
    return mapLivePriceData({
      entity: entityWithCountry,
      lastKnownPrice: priceEntity.lastKnownPrice,
      priceBefore: priceEntity.priceBefore,
      time: priceEntity.time,
    });
  }

  return livePriceData[livePriceDataKey];
};
